import { endOfDay, startOfDay } from 'date-fns';
import qs, { IParseOptions } from 'qs';
import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useAuthContext } from '../../../../providers/AuthProvider';
import { PERMS } from '../../../../routes/permissions';
import { GanttFilter } from './useGanttFilterStore';

export const AVAILABLE_QUERY_PARAMS: Record<keyof AvailableQueryParams, keyof AvailableQueryParams> = {
  view: 'view',
  taskId: 'taskId',
  queueId: 'queueId',
  taskExecutionId: 'taskExecutionId',
  mode: 'mode',
  ganttFrom: 'ganttFrom',
  ganttTo: 'ganttTo',
  ganttFilters: 'ganttFilters',
};

export enum PlanView {
  GANTT = 'gantt',
  KANBAN = 'kanban',
}

export enum GanttMode {
  DEFAULT = 'default',
  WORKER = 'worker',
}

export const GANTT_DATE_FORMAT = 'yyyy-MM-dd-HH';

const parseDate = (start: boolean) => (date?: string) => {
  if (!date) {
    return undefined;
  }

  if (start) {
    return startOfDay(new Date(date)) ?? undefined;
  }

  return endOfDay(new Date(date)) ?? undefined;
};

const GANTT_FILTER_VALUES = new Set<string>(Object.values(GanttFilter));

const parseGanttFilters = (value?: string): GanttFilter[] => {
  if (!value) {
    return [];
  }

  return value.split(',').filter((filter): filter is GanttFilter => GANTT_FILTER_VALUES.has(filter));
};

export const queryParamsScheme = z.object({
  view: z.nativeEnum(PlanView).default(PlanView.GANTT),
  mode: z.nativeEnum(GanttMode).default(GanttMode.DEFAULT),
  taskId: z.number().optional(),
  queueId: z.number().optional(),
  taskExecutionId: z.number().optional(),
  ganttFrom: z.string().optional().transform(parseDate(true)),
  ganttTo: z.string().optional().transform(parseDate(false)),
  ganttFilters: z.string().optional().transform(parseGanttFilters),
});

export type AvailableQueryParams = z.infer<typeof queryParamsScheme>;

type QueryParams<T extends keyof AvailableQueryParams = keyof AvailableQueryParams> = {
  readonly key: T;
  readonly value?: AvailableQueryParams[T] | null | string;
};
const options: IParseOptions = {
  ignoreQueryPrefix: true,
  plainObjects: true,
  interpretNumericEntities: true,
  decoder: (str, defaultDecoder, charset, type) => {
    const decoded = defaultDecoder(str, defaultDecoder, charset);
    if (type === 'value') {
      if (!isNaN(Number(decoded))) {
        return Number(decoded);
      }
    }
    return decoded;
  },
};

export const useProductionPlanQueryParams = () => {
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { hasPermission } = useAuthContext();
  const canViewAssignmentMode = hasPermission(PERMS.PERM_3);

  const queryParams = useMemo(() => {
    const parsed = qs.parse(location.search, options);
    const params = Object.entries(parsed).reduce((acc, [key, value]) => {
      if (value && key in AVAILABLE_QUERY_PARAMS) {
        return {
          ...acc,
          [key]: value,
        };
      }
      return acc;
    }, {});
    const parsedParams = queryParamsScheme.parse(params);

    if (parsedParams.mode === GanttMode.WORKER && !canViewAssignmentMode) {
      return { ...parsedParams, mode: GanttMode.DEFAULT };
    }

    return parsedParams;
  }, [location.search, canViewAssignmentMode]);

  const setQueryParams = useCallback(
    (queryParams: QueryParams[]) => {
      setSearchParams((prev) => {
        const currentState = qs.parse(prev.toString(), options);
        queryParams.forEach(({ key, value }) => {
          if (value) {
            currentState[key] = value;
          } else {
            delete currentState[key];
          }
        });
        return qs.stringify(currentState);
      });
    },
    [setSearchParams],
  );

  return {
    queryParams,
    setQueryParams,
  };
};
